import { useState, useRef, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import SettingsForm from '../settings/SettingsForm'

export default function HomeNav() {
  const navigate = useNavigate()
  const { user, isAuthenticated, signOut } = useAuth()
  const [showUserMenu, setShowUserMenu] = useState(false)
  const [showSettings, setShowSettings] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  // Close menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleSignOut = async () => {
    await signOut()
    setShowUserMenu(false)
    navigate('/')
  }

  return (
    <nav className="fixed top-0 left-0 right-0 flex items-center justify-between px-6 md:px-12 py-3 z-[1000] bg-transparent">
      <div className="flex items-center">
        <Link to="/" className="flex items-center gap-2.5 no-underline text-foreground">
          <svg className="flex-shrink-0" width="28" height="28" viewBox="0 0 32 32" fill="none">
            <path d="M8 20C5.8 20 4 18.2 4 16C4 14.1 5.4 12.5 7.2 12.1C7.1 11.7 7 11.4 7 11C7 8.8 8.8 7 11 7C12.1 7 13.1 7.5 13.8 8.2C14.8 6.9 16.3 6 18 6C20.8 6 23 8.2 23 11C23 11.3 23 11.7 22.9 12C25.2 12.3 27 14.3 27 16.5C27 18.9 25 21 22.5 21" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
          <span className="text-base font-semibold tracking-[-0.01em]">DreamWright</span>
        </Link>
      </div>

      <div className="flex items-center gap-6">
        <a href="#" className="hidden sm:block text-text-muted no-underline text-sm font-medium transition-colors duration-150 hover:text-foreground">Explore</a>
        <a href="#" className="hidden sm:block text-text-muted no-underline text-sm font-medium transition-colors duration-150 hover:text-foreground">Pricing</a>
        <a href="#" className="hidden md:block text-text-muted no-underline text-sm font-medium transition-colors duration-150 hover:text-foreground">About</a>
        
        <div className="flex items-center gap-2">
          <Link
            to="/for-agents"
            className="hidden sm:block text-text-muted no-underline text-sm font-mono transition-colors duration-300 hover:text-foreground cursor-pointer"
          >
            I am NOT human.
          </Link>
          {isAuthenticated ? (
            <>
              <Link
                to="/dashboard"
                className="flex items-center h-8 px-3.5 rounded-md bg-transparent border border-[rgba(255,255,255,0.12)] text-foreground text-sm font-medium cursor-pointer transition-all duration-150 hover:bg-card-hover hover:border-[rgba(255,255,255,0.2)] no-underline"
              >
                My Projects
              </Link>
              
              {/* User menu */}
              <div className="relative" ref={menuRef}>
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex items-center justify-center w-9 h-9 rounded-lg hover:bg-surface-2 transition-all cursor-pointer"
                  title={user?.email || 'Account'}
                >
                  {user?.user_metadata?.avatar_url ? (
                    <img
                      src={user.user_metadata.avatar_url}
                      alt="Avatar"
                      className="w-8 h-8 rounded-lg"
                    />
                  ) : (
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-accent to-primary flex items-center justify-center text-foreground text-sm font-semibold">
                      {user?.email?.charAt(0).toUpperCase() || 'U'}
                    </div>
                  )}
                </button>

                {showUserMenu && (
                  <div className="absolute right-0 mt-2 w-48 bg-background border border-border rounded-lg shadow-[0_8px_30px_rgba(0,0,0,0.8)] py-1 z-50">
                    <div className="px-3 py-2.5 border-b border-[rgba(255,255,255,0.08)]">
                      <p className="text-sm font-medium truncate text-foreground">{user?.user_metadata?.full_name || 'User'}</p>
                      <p className="text-xs text-text-muted truncate mt-0.5">{user?.email}</p>
                    </div>
                    <button
                      onClick={() => {
                        setShowUserMenu(false)
                        setShowSettings(true)
                      }}
                      className="w-full px-3 py-2 text-left text-sm text-text-muted hover:bg-card-hover hover:text-foreground transition-colors duration-150 flex items-center gap-2 cursor-pointer"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      Settings
                    </button>
                    <button
                      onClick={handleSignOut}
                      className="w-full px-3 py-2 text-left text-sm text-text-muted hover:bg-card-hover hover:text-foreground transition-colors duration-150 flex items-center gap-2 cursor-pointer"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                      </svg>
                      Sign out
                    </button>
                  </div>
                )}
              </div>
            </>
          ) : (
            <Link
              to="/login"
              className="inline-flex items-center h-8 px-3.5 bg-white border-none text-black rounded-md text-sm font-medium cursor-pointer transition-all duration-150 hover:bg-gray-200 no-underline"
            >
              Sign In
            </Link>
          )}
        </div>
      </div>

      {/* Settings Modal */}
      {showSettings && (
        <div
          className="fixed inset-0 z-[1001] flex items-center justify-center bg-black/70 backdrop-blur-sm"
          onClick={() => setShowSettings(false)}
        >
          <div
            className="bg-background border border-border rounded-xl shadow-2xl w-full max-w-lg max-h-[80vh] overflow-y-auto p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-foreground">Settings</h2>
              <button
                onClick={() => setShowSettings(false)}
                className="p-1.5 rounded-md hover:bg-surface-2 transition-colors cursor-pointer"
              >
                <svg className="w-5 h-5 text-text-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <SettingsForm onSave={() => setShowSettings(false)} />
          </div>
        </div>
      )}
    </nav>
  )
}
